import React, { useEffect, useMemo, useState } from "react";
import { Receipt, Plus, Filter, TrendingUp, AlertTriangle, DollarSign, Send, Lock, MailCheck, ShieldCheck, ArrowUpRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import InvoiceModal from "@/components/billing/InvoiceModal";
import PaymentReminderModal from "@/components/billing/PaymentReminderModal";
import ClientHistoryDrawer from "@/components/billing/ClientHistoryDrawer";
import { endpoints } from "@/lib/api";
import { fmtMoney, PAYMENT_STATUS_TONE, REMINDER_STATUS_TONE } from "@/components/billing/billingMeta";

const StatCard = ({ label, value, tone, icon: Icon, testId }) => (
  <div data-testid={testId} className="rounded-2xl border border-slate-200/70 dark:border-white/10 bg-white/60 dark:bg-slate-900/40 p-4">
    <div className="flex items-center gap-2 text-slate-400">
      <Icon className="h-3.5 w-3.5" />
      <span className="text-[10px] font-semibold uppercase tracking-wider">{label}</span>
    </div>
    <div className={`text-2xl font-extrabold font-display mt-1 font-mono ${tone}`}>{value}</div>
  </div>
);

export default function BillingPage() {
  const [user, setUser] = useState({ name: "" });
  const [consultants, setConsultants] = useState([]);
  const [authorized, setAuthorized] = useState(null); // null=loading
  const [tab, setTab] = useState("invoices");
  const [invoices, setInvoices] = useState([]);
  const [reminders, setReminders] = useState([]);
  const [summary, setSummary] = useState(null);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [clientFilter, setClientFilter] = useState("all");
  const [invoiceModal, setInvoiceModal] = useState({ open: false, initial: null });
  const [reminderModal, setReminderModal] = useState({ open: false, invoice: null, reminder: null });
  const [historyDrawer, setHistoryDrawer] = useState({ open: false, client: null });

  const load = () => Promise.all([
    endpoints.listInvoices().then(setInvoices),
    endpoints.listReminders().then(setReminders),
    endpoints.billingSummary().then(setSummary),
  ]).catch(() => {});

  useEffect(() => {
    endpoints.me().then((u) => {
      setUser(u);
      endpoints.listConsultants().then((cs) => {
        setConsultants(cs);
        const me = cs.find((c) => c.name === u.name);
        setAuthorized(u.canonical_role === "admin" || u.canonical_role === "staff" || !!me?.is_manager);
      });
    });
  }, []);

  useEffect(() => {
    if (authorized) load();
  }, [authorized]);

  const clients = useMemo(() => Array.from(new Set(invoices.map((i) => i.client))).sort(), [invoices]);

  const filteredInvoices = useMemo(() => {
    return invoices.filter((i) => {
      if (statusFilter !== "all" && i.status !== statusFilter) return false;
      if (clientFilter !== "all" && i.client !== clientFilter) return false;
      if (q && !`${i.invoice_number} ${i.client} ${i.description}`.toLowerCase().includes(q.toLowerCase())) return false;
      return true;
    });
  }, [invoices, statusFilter, clientFilter, q]);

  const pendingReminders = reminders.filter((r) => r.status === "Pending Approval");

  if (authorized === null) return <div className="p-8 text-sm text-slate-500">Loading...</div>;

  if (!authorized) {
    return (
      <div className="p-6 lg:p-10 max-w-3xl mx-auto">
        <div className="glass-card rounded-3xl p-10 text-center">
          <div className="mx-auto h-12 w-12 rounded-2xl bg-rose-50 dark:bg-rose-500/15 text-rose-600 dark:text-rose-300 flex items-center justify-center mb-4">
            <Lock className="h-5 w-5" />
          </div>
          <h1 className="font-display text-xl font-extrabold mb-1">Authorized staff only</h1>
          <p className="text-sm text-slate-500">Client Billing is restricted to ISOQuest management. Ask an administrator for access.</p>
        </div>
      </div>
    );
  }

  const openRemind = (inv) => setReminderModal({ open: true, invoice: inv, reminder: null });
  const openReviewReminder = (r) => {
    const inv = invoices.find((i) => i.id === r.invoice_id) || {
      invoice_number: r.invoice_number, client: r.client, due_date: r.due_date, amount: r.outstanding_amount, amount_paid: 0,
    };
    setReminderModal({ open: true, invoice: inv, reminder: r });
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-[1600px] mx-auto">
      <div className="glass-card rounded-3xl p-4 sm:p-5 mb-5 flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-2xl brand-gradient flex items-center justify-center shadow-md shadow-indigo-500/25">
            <Receipt className="h-5 w-5 text-white" />
          </div>
          <div>
            <h1 className="font-display text-lg sm:text-xl font-extrabold tracking-tight">Client Billing</h1>
            <p className="text-xs text-slate-500">Invoices, cash flow and payment reminders</p>
          </div>
          <Badge variant="outline" className="ml-2 gap-1 text-[10px] rounded-full bg-indigo-50 text-indigo-700 border-indigo-200">
            <ShieldCheck className="h-2.5 w-2.5" /> Staff only
          </Badge>
        </div>
        <Button data-testid="billing-new-invoice-button" className="brand-gradient text-white ml-auto" onClick={() => setInvoiceModal({ open: true, initial: null })}>
          <Plus className="h-4 w-4 mr-1" /> New invoice
        </Button>
      </div>

      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
          <StatCard testId="billing-stat-outstanding" label="Total outstanding" value={fmtMoney(summary.total_outstanding)} tone="text-slate-800 dark:text-white" icon={DollarSign} />
          <StatCard testId="billing-stat-overdue" label={`Overdue · ${summary.overdue_count}`} value={fmtMoney(summary.total_overdue)} tone="text-rose-600" icon={AlertTriangle} />
          <StatCard testId="billing-stat-paid" label="Paid this month" value={fmtMoney(summary.paid_this_month)} tone="text-emerald-600" icon={TrendingUp} />
          <StatCard testId="billing-stat-invoices" label="Total invoices" value={summary.invoices_count} tone="text-indigo-600" icon={Receipt} />
        </div>
      )}

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList data-testid="billing-tabs" className="h-10 bg-slate-100 dark:bg-slate-800 mb-4">
          <TabsTrigger value="invoices" data-testid="billing-tab-invoices" className="text-xs h-8">Invoices</TabsTrigger>
          <TabsTrigger value="clients" data-testid="billing-tab-clients" className="text-xs h-8">By client</TabsTrigger>
          <TabsTrigger value="reminders" data-testid="billing-tab-reminders" className="text-xs h-8 relative">
            Reminders
            {pendingReminders.length > 0 && <span className="ml-1.5 rounded-full bg-amber-500 text-white text-[9px] px-1.5 py-0.5 font-bold">{pendingReminders.length}</span>}
          </TabsTrigger>
        </TabsList>

        {tab === "invoices" && (
          <>
            <div className="glass-card rounded-3xl p-4 mb-4 flex flex-wrap items-center gap-3">
              <Input data-testid="billing-search-input" placeholder="Search invoices..." value={q} onChange={(e) => setQ(e.target.value)} className="max-w-xs h-9" />
              <div className="flex items-center gap-1.5">
                <Filter className="h-3.5 w-3.5 text-slate-400" />
                <Select value={clientFilter} onValueChange={setClientFilter}>
                  <SelectTrigger data-testid="billing-client-filter" className="h-9 w-[180px] text-xs"><SelectValue placeholder="All clients" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All clients</SelectItem>
                    {clients.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger data-testid="billing-status-filter" className="h-9 w-[150px] text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All statuses</SelectItem>
                    <SelectItem value="Draft">Draft</SelectItem>
                    <SelectItem value="Sent">Sent</SelectItem>
                    <SelectItem value="Paid">Paid</SelectItem>
                    <SelectItem value="Partially Paid">Partially Paid</SelectItem>
                    <SelectItem value="Overdue">Overdue</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="glass-card rounded-3xl overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm min-w-[900px]">
                  <thead className="bg-slate-50/70 dark:bg-slate-900/60 text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
                    <tr>
                      <th className="text-left px-4 py-3">Invoice #</th>
                      <th className="text-left px-4 py-3">Client</th>
                      <th className="text-left px-4 py-3">Description</th>
                      <th className="text-right px-4 py-3">Amount</th>
                      <th className="text-right px-4 py-3">Paid</th>
                      <th className="text-right px-4 py-3">Outstanding</th>
                      <th className="text-left px-4 py-3">Due</th>
                      <th className="text-center px-4 py-3">Status</th>
                      <th className="text-right px-4 py-3">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredInvoices.map((inv) => (
                      <tr
                        key={inv.id}
                        data-testid={`invoice-row-${inv.id}`}
                        className="border-t border-slate-100 dark:border-white/5 hover:bg-indigo-50/40 dark:hover:bg-indigo-500/10 transition-colors"
                      >
                        <td className="px-4 py-3 font-mono text-xs font-semibold cursor-pointer" onClick={() => setInvoiceModal({ open: true, initial: inv })}>{inv.invoice_number}</td>
                        <td className="px-4 py-3">
                          <button
                            data-testid={`invoice-client-${inv.id}`}
                            onClick={() => setHistoryDrawer({ open: true, client: inv.client })}
                            className="text-left font-semibold hover:text-indigo-600 hover:underline"
                          >
                            {inv.client}
                          </button>
                        </td>
                        <td className="px-4 py-3 text-slate-500 max-w-[200px] truncate">{inv.description}</td>
                        <td className="px-4 py-3 text-right font-mono">{fmtMoney(inv.amount)}</td>
                        <td className="px-4 py-3 text-right font-mono text-emerald-600">{fmtMoney(inv.amount_paid)}</td>
                        <td className="px-4 py-3 text-right font-mono font-bold">{fmtMoney(inv.outstanding)}</td>
                        <td className="px-4 py-3 text-xs">{inv.due_date}</td>
                        <td className="px-4 py-3 text-center">
                          <Badge variant="outline" className={`text-[10px] rounded-full ${PAYMENT_STATUS_TONE[inv.status] || ""}`}>{inv.status}</Badge>
                        </td>
                        <td className="px-4 py-3 text-right">
                          {inv.status !== "Paid" && inv.status !== "Draft" && (
                            <Button
                              data-testid={`invoice-remind-${inv.id}`}
                              size="sm"
                              variant={inv.status === "Overdue" ? "default" : "outline"}
                              className={`h-7 text-xs ${inv.status === "Overdue" ? "bg-rose-600 hover:bg-rose-700 text-white" : ""}`}
                              onClick={(e) => { e.stopPropagation(); openRemind(inv); }}
                            >
                              <Send className="h-3 w-3 mr-1" /> Remind
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {filteredInvoices.length === 0 && <div className="text-center py-10 text-sm text-slate-500">No invoices match your filters.</div>}
            </div>
          </>
        )}

        {tab === "clients" && summary && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {summary.clients.map((c) => (
              <button
                key={c.client}
                data-testid={`client-card-${c.client}`}
                onClick={() => setHistoryDrawer({ open: true, client: c.client })}
                className="glass-card rounded-2xl p-4 text-left card-hover"
              >
                <div className="flex items-center justify-between">
                  <div className="font-semibold">{c.client}</div>
                  {c.overdue > 0 && (
                    <Badge className="bg-rose-100 text-rose-700 border-rose-200 rounded-full text-[10px]">
                      <AlertTriangle className="h-2.5 w-2.5 mr-1" />{c.overdue} overdue
                    </Badge>
                  )}
                </div>
                <div className="text-[11px] text-slate-500 mt-1">{c.invoices} invoices</div>
                <div className="grid grid-cols-3 gap-2 mt-3">
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-slate-400">Billed</div>
                    <div className="text-sm font-bold font-mono">{fmtMoney(c.total_billed)}</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-slate-400">Paid</div>
                    <div className="text-sm font-bold font-mono text-emerald-600">{fmtMoney(c.total_paid)}</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-slate-400">Outstanding</div>
                    <div className={`text-sm font-bold font-mono ${c.outstanding > 0 ? "text-rose-600" : "text-slate-400"}`}>{fmtMoney(c.outstanding)}</div>
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}

        {tab === "reminders" && (
          <div className="space-y-2">
            {reminders.length === 0 ? (
              <div className="glass-card rounded-3xl p-10 text-center text-sm text-slate-500">
                <MailCheck className="h-8 w-8 mx-auto text-slate-300 mb-2" />
                No reminders prepared yet.
              </div>
            ) : reminders.map((r) => (
              <div key={r.id} data-testid={`reminder-row-${r.id}`} className="glass-card rounded-2xl p-4 flex items-start gap-3">
                <div className="h-10 w-10 rounded-xl bg-indigo-50 dark:bg-indigo-500/15 text-indigo-600 dark:text-indigo-300 flex items-center justify-center shrink-0">
                  <MailCheck className="h-4.5 w-4.5" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-semibold">{r.client}</span>
                    <span className="font-mono text-xs text-slate-500">{r.invoice_number}</span>
                    <Badge variant="outline" className={`text-[10px] rounded-full ${REMINDER_STATUS_TONE[r.status] || ""}`}>{r.status}</Badge>
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5">{r.subject}</div>
                  <div className="text-[11px] text-slate-400 mt-1">
                    Prepared by <span className="font-semibold">{r.prepared_by}</span>
                    {r.approved_by && <> · sent by <span className="font-semibold">{r.approved_by}</span></>}
                    {r.sent_at && <> · {new Date(r.sent_at).toLocaleDateString()}</>}
                  </div>
                </div>
                <Button data-testid={`reminder-open-${r.id}`} size="sm" variant="outline" className="h-8" onClick={() => openReviewReminder(r)}>
                  Open <ArrowUpRight className="h-3 w-3 ml-1" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </Tabs>

      <InvoiceModal
        open={invoiceModal.open}
        initial={invoiceModal.initial}
        onOpenChange={(o) => setInvoiceModal({ open: o, initial: invoiceModal.initial })}
        onChanged={() => { load(); setInvoiceModal({ open: false, initial: null }); }}
      />

      <PaymentReminderModal
        open={reminderModal.open}
        invoice={reminderModal.invoice}
        reminder={reminderModal.reminder}
        currentUser={user}
        onOpenChange={(o) => setReminderModal({ open: o, invoice: reminderModal.invoice, reminder: reminderModal.reminder })}
        onChanged={() => load()}
      />

      <ClientHistoryDrawer
        open={historyDrawer.open}
        client={historyDrawer.client}
        onOpenChange={(o) => setHistoryDrawer({ open: o, client: historyDrawer.client })}
        onRemind={(inv) => {
          setHistoryDrawer({ open: false, client: null });
          openRemind(inv);
        }}
      />
    </div>
  );
}
